import { Injectable } from '@angular/core';

import { KnowledgeBaseArticle, ListRequest, ListResponse } from 'src/models';

import { ApiService } from './api.service';
import { GenericObjectsService } from './generic-objects.service';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeBaseArticlesService extends GenericObjectsService<KnowledgeBaseArticle> {
  protected override baseUrl = '/knowledge-base/articles';

  constructor(apiService: ApiService) {
    super(apiService);
  }

  async listAllStubs(params: ListRequest = {}): Promise<Partial<KnowledgeBaseArticle>[]> {
    let fetchedArticles: Partial<KnowledgeBaseArticle>[] = [];
    let nextCursor: string | undefined = undefined;
    const limit = 100;
    let response: ListResponse<Partial<KnowledgeBaseArticle>>;

    // Ensure essential fields are selected, allow overriding/adding via params
    const baseFields = ['id', 'title', 'collectionId', 'status', 'updatedAt'];
    const finalFields = params.fields ? [...new Set([...baseFields, ...params.fields])] : baseFields;

    const baseParams: ListRequest = {
      ...params,
      fields: finalFields,
      limit: params.limit ?? limit,
    };
    delete baseParams.nextCursor;
    delete baseParams.previousCursor;

    let currentParams: ListRequest;
    let hasNextPage = true;

    do {
      try {
        currentParams = { ...baseParams };
        if (nextCursor) {
          currentParams.cursor = nextCursor;
        }

        response = await this.list(currentParams);

        if (response?.data) {
          fetchedArticles = fetchedArticles.concat(response.data);
        }

        nextCursor = response?.cursors?.next;
        hasNextPage = response?.cursors?.hasNext ?? false;

        if (!hasNextPage) {
          break;
        }
      } catch (listError) {
        console.error(
          `Error fetching article stubs page with cursor ${currentParams?.cursor}:`,
          listError,
          currentParams,
        );
        hasNextPage = false;
        throw listError;
      }
    } while (hasNextPage);

    return fetchedArticles;
  }

  async bulkDelete(articleIds: string[]): Promise<void> {
    await this.apiService.deletePromise<void>(`${this.baseUrl}/bulk`, { articleIds });
  }

  async bulkMove(articleIds: string[], targetCollectionId: string | null): Promise<void> {
    const payload = {
      articleIds,
      targetCollectionId,
    };
    await this.apiService.postPromise<void>(`${this.baseUrl}/bulk-move`, payload);
  }
}
